import type { AxiosResponse } from 'axios';
import { Platform } from 'react-native';
import RNFS from 'react-native-fs';
import FileViewer from 'react-native-file-viewer';
import Share from 'react-native-share';
import { apiClient } from '../api/apiClient';
import { API_ENDPOINTS } from '../api/apiEndpoints';
import { normalizeApiError } from '../api/apiError';
import { isConfirmedReportCode } from '../config/reportCatalogue';
import {
  PROJECT_TASK_REPORT_CODE,
  type ProjectTaskReportQuery,
} from '../types/projectReport';
import { projectTaskReportParams } from '../utils/projectReport';
import type {
  ReportDownload,
  ReportExportView,
  ReportFileFormat,
  ReportFilterValue,
} from '../types/report';
import {
  isSupportedReportMimeType,
  parseContentDispositionFileName,
  sanitizeReportFileName,
} from '../utils/reportFileName';

/**
 * Authenticated binary download path:
 * Uses the existing Axios apiClient with responseType 'arraybuffer' so the
 * request interceptor attaches Bearer tokens and the 401 refresh interceptor
 * retries once. File bytes are written privately via react-native-fs and are
 * never returned to screen state.
 */

const REPORTS_DIR_NAME = 'reports';

function createError(message: string, code: string): Error {
  return Object.assign(new Error(message), { code });
}

function mimeForFormat(format: ReportFileFormat): string {
  if (format === 'PDF') return 'application/pdf';
  return format === 'CSV'
    ? 'text/csv'
    : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
}

async function ensureReportsDirectory(): Promise<string> {
  const base =
    Platform.OS === 'ios'
      ? RNFS.DocumentDirectoryPath
      : RNFS.DocumentDirectoryPath;
  const dir = `${base}/${REPORTS_DIR_NAME}`;
  const exists = await RNFS.exists(dir);
  if (!exists) {
    await RNFS.mkdir(dir);
  }
  return dir;
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode(...chunk);
  }
  if (typeof globalThis.btoa === 'function') {
    return globalThis.btoa(binary);
  }
  // Jest / Node fallback
  return Buffer.from(binary, 'binary').toString('base64');
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  if (typeof globalThis.btoa === 'function') return globalThis.btoa(binary);
  return Buffer.from(binary, 'binary').toString('base64');
}

function utf8(value: string): Uint8Array {
  return new TextEncoder().encode(value);
}

function csvCell(value: ReportFilterValue): string {
  let text = value == null ? '' : String(value);
  // Prevent spreadsheet formula injection from exported user-controlled text.
  if (/^[=+\-@]/.test(text)) text = `'${text}`;
  return `"${text.replace(/"/g, '""')}"`;
}

function xmlText(value: ReportFilterValue): string {
  return (value == null ? '' : String(value))
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff;
  for (const byte of bytes) {
    crc ^= byte;
    for (let bit = 0; bit < 8; bit += 1) {
      crc = (crc >>> 1) ^ (0xedb88320 & -(crc & 1));
    }
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function u16(value: number): number[] {
  return [value & 0xff, (value >>> 8) & 0xff];
}

function u32(value: number): number[] {
  return [
    value & 0xff,
    (value >>> 8) & 0xff,
    (value >>> 16) & 0xff,
    (value >>> 24) & 0xff,
  ];
}

/** Minimal uncompressed ZIP writer; sufficient for a standards-compliant XLSX. */
function zip(files: Array<{ name: string; content: string }>): Uint8Array {
  const local: number[] = [];
  const central: number[] = [];
  let offset = 0;
  for (const file of files) {
    const name = utf8(file.name);
    const data = utf8(file.content);
    const crc = crc32(data);
    const header = [
      ...u32(0x04034b50),
      ...u16(20),
      ...u16(0x0800),
      ...u16(0),
      ...u16(0),
      ...u16(0),
      ...u32(crc),
      ...u32(data.length),
      ...u32(data.length),
      ...u16(name.length),
      ...u16(0),
      ...name,
    ];
    local.push(...header, ...data);
    central.push(
      ...u32(0x02014b50),
      ...u16(20),
      ...u16(20),
      ...u16(0x0800),
      ...u16(0),
      ...u16(0),
      ...u16(0),
      ...u32(crc),
      ...u32(data.length),
      ...u32(data.length),
      ...u16(name.length),
      ...u16(0),
      ...u16(0),
      ...u16(0),
      ...u16(0),
      ...u32(0),
      ...u32(offset),
      ...name,
    );
    offset += header.length + data.length;
  }
  const centralOffset = local.length;
  const end = [
    ...u32(0x06054b50),
    ...u16(0),
    ...u16(0),
    ...u16(files.length),
    ...u16(files.length),
    ...u32(central.length),
    ...u32(centralOffset),
    ...u16(0),
  ];
  return Uint8Array.from([...local, ...central, ...end]);
}

function xlsxBytes(view: ReportExportView): Uint8Array {
  const cell = (value: ReportFilterValue) =>
    `<c t="inlineStr"><is><t xml:space="preserve">${xmlText(
      value,
    )}</t></is></c>`;
  const rows = [
    view.columns.map(column => cell(column.label)),
    ...view.rows.map(row =>
      view.columns.map(column => cell(row[column.key] ?? null)),
    ),
  ]
    .map((cells, index) => `<row r="${index + 1}">${cells.join('')}</row>`)
    .join('');
  return zip([
    {
      name: '[Content_Types].xml',
      content:
        '<?xml version="1.0" encoding="UTF-8"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/></Types>',
    },
    {
      name: '_rels/.rels',
      content:
        '<?xml version="1.0" encoding="UTF-8"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>',
    },
    {
      name: 'xl/workbook.xml',
      content:
        '<?xml version="1.0" encoding="UTF-8"?><workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="Report" sheetId="1" r:id="rId1"/></sheets></workbook>',
    },
    {
      name: 'xl/_rels/workbook.xml.rels',
      content:
        '<?xml version="1.0" encoding="UTF-8"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/></Relationships>',
    },
    {
      name: 'xl/worksheets/sheet1.xml',
      content: `<?xml version="1.0" encoding="UTF-8"?><worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetData>${rows}</sheetData></worksheet>`,
    },
  ]);
}

export async function createDisplayedReport(input: {
  reportCode: string;
  reportName?: string;
  format: 'CSV' | 'XLSX';
  view: ReportExportView;
}): Promise<ReportDownload> {
  const fileName = sanitizeReportFileName(null, input.format, input.reportCode);
  const dir = await ensureReportsDirectory();
  const finalPath = `${dir}/${fileName}`;
  if (await RNFS.exists(finalPath)) await RNFS.unlink(finalPath);
  const bytes =
    input.format === 'XLSX'
      ? xlsxBytes(input.view)
      : utf8(
          [
            input.view.columns.map(column => csvCell(column.label)).join(','),
            ...input.view.rows.map(row =>
              input.view.columns
                .map(column => csvCell(row[column.key] ?? null))
                .join(','),
            ),
          ].join('\r\n'),
        );
  await RNFS.writeFile(finalPath, bytesToBase64(bytes), 'base64');
  return {
    downloadId: `dl_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`,
    reportCode: input.reportCode,
    reportName: input.reportName || input.reportCode,
    format: input.format,
    localFilePath: finalPath,
    fileName,
    mimeType: mimeForFormat(input.format),
    sizeBytes: bytes.length,
    downloadedAt: new Date().toISOString(),
  };
}

function looksLikeJsonError(buffer: ArrayBuffer): boolean {
  if (buffer.byteLength < 2 || buffer.byteLength > 64_000) return false;
  const bytes = new Uint8Array(buffer);
  let start = 0;
  while (start < bytes.length && bytes[start] <= 32) start += 1;
  if (bytes[start] !== 0x7b /* { */) return false;
  try {
    const text = String.fromCharCode(
      ...bytes.subarray(0, Math.min(bytes.length, 4000)),
    );
    const parsed = JSON.parse(text) as { success?: unknown; code?: unknown };
    return (
      parsed != null &&
      (parsed.success === false || typeof parsed.code === 'string')
    );
  } catch {
    return false;
  }
}

function toArrayBuffer(data: unknown): ArrayBuffer {
  if (data instanceof ArrayBuffer) return data;
  if (ArrayBuffer.isView(data)) {
    const view = data as ArrayBufferView;
    return view.buffer.slice(
      view.byteOffset,
      view.byteOffset + view.byteLength,
    ) as ArrayBuffer;
  }
  throw createError('The downloaded report is invalid.', 'REPORT_FILE_INVALID');
}

function parseJsonErrorFromBuffer(buffer: ArrayBuffer): Error {
  try {
    const text = String.fromCharCode(...new Uint8Array(buffer));
    const parsed = JSON.parse(text) as {
      message?: string;
      code?: string;
    };
    return createError(
      parsed.message || 'The report could not be generated.',
      typeof parsed.code === 'string'
        ? parsed.code
        : 'REPORT_GENERATION_FAILED',
    );
  } catch {
    return createError(
      'The downloaded report is invalid.',
      'REPORT_FILE_INVALID',
    );
  }
}

/**
 * With responseType 'arraybuffer', a non-2xx JSON error body arrives as bytes,
 * so normalizeApiError cannot read its message/code. Decode it here so the
 * API's own reason (e.g. PDF_GENERATOR_UNAVAILABLE, ACCESS_DENIED) reaches
 * the user instead of a generic failure.
 */
function errorFromBinaryErrorResponse(error: unknown): Error | null {
  const response = (error as { response?: { data?: unknown; status?: number } })
    ?.response;
  if (!response || response.data == null) return null;
  let buffer: ArrayBuffer;
  try {
    buffer = toArrayBuffer(response.data);
  } catch {
    return null;
  }
  if (!looksLikeJsonError(buffer)) return null;
  const parsed = parseJsonErrorFromBuffer(buffer);
  return Object.assign(parsed, { statusCode: response.status });
}

function looksLikeHtml(buffer: ArrayBuffer): boolean {
  if (buffer.byteLength < 15) return false;
  const sample = String.fromCharCode(
    ...new Uint8Array(buffer).subarray(0, Math.min(buffer.byteLength, 200)),
  ).toLowerCase();
  return sample.includes('<!doctype html') || sample.includes('<html');
}

/** Axios options shared by every authenticated binary report download. */
function fileRequestConfig(signal?: AbortSignal) {
  return {
    responseType: 'arraybuffer' as const,
    signal,
    // Longer timeout for exports; still uses auth interceptors.
    timeout: Math.max(apiClient.defaults.timeout ?? 30000, 60000),
    // Avoid treating JSON error envelopes as success when status is 2xx (unlikely).
    validateStatus: (status: number) => status >= 200 && status < 300,
    transformResponse: [(data: unknown) => data],
  };
}

type BrowserAnchor = {
  href: string;
  download: string;
  click: () => void;
  remove: () => void;
};

type BrowserDocument = {
  createElement: (tag: 'a') => BrowserAnchor;
  body: { appendChild: (node: BrowserAnchor) => void };
};

/** Browser builds have no app file system: hand the bytes to the browser. */
function saveBytesInBrowser(bytes: ArrayBuffer, fileName: string, mimeType: string) {
  const browserDocument = (globalThis as unknown as { document: BrowserDocument })
    .document;
  const blob = new Blob([new Uint8Array(bytes) as unknown as Blob], {
    type: mimeType,
    lastModified: Date.now(),
  });
  const url = URL.createObjectURL(blob);
  const link = browserDocument.createElement('a');
  link.href = url;
  link.download = fileName;
  browserDocument.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function downloadReport(input: {
  reportCode: string;
  reportName?: string;
  format: 'CSV' | 'XLSX';
  filters: Record<string, ReportFilterValue>;
  view?: ReportExportView;
  signal?: AbortSignal;
}): Promise<ReportDownload> {
  if (!isConfirmedReportCode(input.reportCode)) {
    throw createError('Report code is not supported', 'INVALID_REPORT_CODE');
  }
  if (input.format !== 'CSV' && input.format !== 'XLSX') {
    throw createError(
      'This export format is not available.',
      'INVALID_REPORT_FORMAT',
    );
  }

  // Never accept a server path or free-form URL from callers.
  const endpoint = API_ENDPOINTS.REPORTS.RUN(input.reportCode);
  return saveReportFile({
    reportCode: input.reportCode,
    reportName: input.reportName,
    format: input.format,
    request: () =>
      apiClient.post<ArrayBuffer>(
        endpoint,
        {
          format: input.format,
          download: true,
          filters: input.filters,
          ...(input.view ? { view: input.view } : {}),
        },
        fileRequestConfig(input.signal),
      ),
  });
}

/**
 * Project task report download (PDF / CSV / XLSX) generated by the API from the
 * same query the on-screen report uses, so the file matches the view.
 */
export async function downloadProjectTaskReport(input: {
  format: ReportFileFormat;
  query: ProjectTaskReportQuery;
  signal?: AbortSignal;
}): Promise<ReportDownload> {
  if (!['PDF', 'CSV', 'XLSX'].includes(input.format)) {
    throw createError(
      'This export format is not available.',
      'INVALID_REPORT_FORMAT',
    );
  }
  return saveReportFile({
    reportCode: PROJECT_TASK_REPORT_CODE,
    reportName: 'Project Task Report',
    format: input.format,
    saveInBrowser: Platform.OS === 'web',
    request: () =>
      apiClient.get<ArrayBuffer>(API_ENDPOINTS.PROJECTS.REPORT_TASKS(input.query.projectId), {
        ...fileRequestConfig(input.signal),
        params: { ...projectTaskReportParams(input.query), format: input.format },
      }),
  });
}

export type EmployeeOverallWeeklyDownloadQuery = {
  weekStart: string;
  weekEnd: string;
  search?: string;
};

/**
 * Employee Overall Weekly Report download (XLSX / PDF) from the same attendance
 * endpoint and week filters as the on-screen grid.
 */
export async function downloadEmployeeOverallWeeklyReport(input: {
  format: 'XLSX' | 'PDF';
  query: EmployeeOverallWeeklyDownloadQuery;
  signal?: AbortSignal;
}): Promise<ReportDownload> {
  if (input.format !== 'XLSX' && input.format !== 'PDF') {
    throw createError(
      'This export format is not available.',
      'INVALID_REPORT_FORMAT',
    );
  }
  const params: Record<string, string> = {
    weekStart: input.query.weekStart,
    weekEnd: input.query.weekEnd,
    format: input.format,
  };
  if (input.query.search?.trim()) params.search = input.query.search.trim();

  return saveReportFile({
    reportCode: 'EMPLOYEE_OVERALL_WEEKLY',
    reportName: 'Employee Overall Weekly Report',
    format: input.format,
    saveInBrowser: Platform.OS === 'web',
    request: () =>
      apiClient.get<ArrayBuffer>(
        API_ENDPOINTS.ATTENDANCE.EMPLOYEE_OVERALL_WEEKLY_REPORT_DOWNLOAD,
        {
          ...fileRequestConfig(input.signal),
          params,
        },
      ),
  });
}

async function saveReportFile(input: {
  reportCode: string;
  reportName?: string;
  format: ReportFileFormat;
  saveInBrowser?: boolean;
  request: () => Promise<AxiosResponse<ArrayBuffer>>;
}): Promise<ReportDownload> {
  let partPath: string | null = null;

  try {
    const response = await input.request();

    const bytes = toArrayBuffer(response.data);

    const contentTypeHeader =
      (response.headers?.['content-type'] as string | undefined) ||
      (response.headers?.['Content-Type'] as string | undefined) ||
      '';

    if (
      looksLikeJsonError(bytes) ||
      contentTypeHeader.includes('application/json')
    ) {
      throw parseJsonErrorFromBuffer(bytes);
    }
    if (looksLikeHtml(bytes) || contentTypeHeader.includes('text/html')) {
      throw createError(
        'The downloaded report is invalid.',
        'REPORT_FILE_INVALID',
      );
    }
    if (
      !isSupportedReportMimeType(
        contentTypeHeader || mimeForFormat(input.format),
        input.format,
      )
    ) {
      // Backend always sets correct MIME; reject unexpected types.
      if (
        contentTypeHeader &&
        !isSupportedReportMimeType(contentTypeHeader, input.format)
      ) {
        throw createError(
          'The downloaded report is invalid.',
          'REPORT_FILE_INVALID',
        );
      }
    }

    const disposition =
      (response.headers?.['content-disposition'] as string | undefined) ||
      (response.headers?.['Content-Disposition'] as string | undefined);
    const serverName = parseContentDispositionFileName(disposition);
    const fileName = sanitizeReportFileName(
      serverName,
      input.format,
      input.reportCode,
    );

    if (input.saveInBrowser) {
      saveBytesInBrowser(bytes, fileName, mimeForFormat(input.format));
      return {
        downloadId: `dl_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`,
        reportCode: input.reportCode,
        reportName: input.reportName || input.reportCode,
        format: input.format,
        localFilePath: '',
        fileName,
        mimeType: mimeForFormat(input.format),
        sizeBytes: bytes.byteLength,
        downloadedAt: new Date().toISOString(),
      };
    }

    const dir = await ensureReportsDirectory();
    const finalPath = `${dir}/${fileName}`;
    partPath = `${finalPath}.part`;

    if (await RNFS.exists(partPath)) {
      await RNFS.unlink(partPath).catch(() => undefined);
    }
    if (await RNFS.exists(finalPath)) {
      await RNFS.unlink(finalPath).catch(() => undefined);
    }

    const base64 = arrayBufferToBase64(bytes);
    await RNFS.writeFile(partPath, base64, 'base64');
    await RNFS.moveFile(partPath, finalPath);
    partPath = null;

    const downloadId = `dl_${Date.now()}_${Math.random()
      .toString(36)
      .slice(2, 10)}`;
    const sizeBytes = bytes.byteLength;

    if (__DEV__) {
      console.info('[reports] download complete', {
        reportCode: input.reportCode,
        format: input.format,
        status: response.status,
        sizeBytes,
      });
    }

    return {
      downloadId,
      reportCode: input.reportCode,
      reportName: input.reportName || input.reportCode,
      format: input.format,
      localFilePath: finalPath,
      fileName,
      mimeType: mimeForFormat(input.format),
      sizeBytes,
      downloadedAt: new Date().toISOString(),
    };
  } catch (error) {
    if (partPath) {
      await RNFS.unlink(partPath).catch(() => undefined);
    }
    if (
      typeof error === 'object' &&
      error !== null &&
      'code' in error &&
      (error as { code?: string }).code === 'ERR_CANCELED'
    ) {
      throw createError(
        'The report download was canceled.',
        'REPORT_DOWNLOAD_CANCELED',
      );
    }
    const bodyError = errorFromBinaryErrorResponse(error);
    if (bodyError) throw bodyError;
    if (
      typeof error === 'object' &&
      error !== null &&
      'code' in error &&
      typeof (error as { code?: unknown }).code === 'string' &&
      String((error as { code: string }).code).startsWith('REPORT_')
    ) {
      throw error;
    }
    if (
      typeof error === 'object' &&
      error !== null &&
      'code' in error &&
      [
        'INVALID_REPORT_CODE',
        'INVALID_REPORT_FORMAT',
        'ACCESS_DENIED',
        'VALIDATION_ERROR',
        'REPORT_TOO_LARGE',
        'REPORT_EXPORT_LIMIT_REACHED',
        'REQUEST_TIMEOUT',
      ].includes((error as { code: string }).code)
    ) {
      throw error;
    }
    const apiError = normalizeApiError(error);
    throw createError(
      apiError.message || 'The report could not be saved.',
      apiError.code || 'REPORT_FILE_WRITE_FAILED',
    );
  }
}

export async function fileExists(localFilePath: string): Promise<boolean> {
  if (!localFilePath || localFilePath.includes('..')) return false;
  try {
    return await RNFS.exists(localFilePath);
  } catch {
    return false;
  }
}

function fileNameFromPath(localFilePath: string, fallback: string): string {
  const name = localFilePath.split('/').pop()?.trim();
  return name || fallback;
}

function asFileUrl(path: string): string {
  return path.startsWith('file://') ? path : `file://${path}`;
}

/**
 * react-native-share's FileProvider only exposes cache/ and Download/.
 * Reports are stored in the app files dir, so copy into cache before sharing.
 */
async function copyReportToCache(
  localFilePath: string,
  fileName: string,
): Promise<string> {
  const dir = `${RNFS.CachesDirectoryPath}/${REPORTS_DIR_NAME}`;
  if (!(await RNFS.exists(dir))) {
    await RNFS.mkdir(dir);
  }
  const dest = `${dir}/${fileName}`;
  if (await RNFS.exists(dest)) {
    await RNFS.unlink(dest).catch(() => undefined);
  }
  await RNFS.copyFile(localFilePath, dest);
  return dest;
}

export async function openReportFile(input: {
  localFilePath: string;
  mimeType: string;
  format: ReportFileFormat;
  fileName?: string;
}): Promise<void> {
  if (!(await fileExists(input.localFilePath))) {
    throw createError(
      'The downloaded report is no longer available.',
      'REPORT_FILE_NOT_AVAILABLE',
    );
  }
  if (!isSupportedReportMimeType(input.mimeType, input.format)) {
    throw createError(
      'No compatible application is available to open this report.',
      'REPORT_OPEN_NOT_SUPPORTED',
    );
  }
  const displayName =
    input.fileName || fileNameFromPath(input.localFilePath, 'Report');
  try {
    await FileViewer.open(input.localFilePath, {
      displayName,
      showOpenWithDialog: true,
      // Never send the user to Play Store when no viewer is found.
      showAppsSuggestions: false,
    });
  } catch {
    try {
      await shareReportFile({
        localFilePath: input.localFilePath,
        mimeType: input.mimeType,
        fileName: displayName,
        format: input.format,
      });
    } catch {
      throw createError(
        'No compatible application is available to open this report.',
        'REPORT_OPEN_NOT_SUPPORTED',
      );
    }
  }
}

export async function shareReportFile(input: {
  localFilePath: string;
  mimeType: string;
  fileName: string;
  format: ReportFileFormat;
}): Promise<void> {
  if (!(await fileExists(input.localFilePath))) {
    throw createError(
      'The downloaded report is no longer available.',
      'REPORT_FILE_NOT_AVAILABLE',
    );
  }
  if (!isSupportedReportMimeType(input.mimeType, input.format)) {
    throw createError('The report could not be shared.', 'REPORT_SHARE_FAILED');
  }

  const sharePath = await copyReportToCache(
    input.localFilePath,
    input.fileName,
  );

  try {
    await Share.open({
      title: 'Share Report',
      url: asFileUrl(sharePath),
      type: input.mimeType,
      failOnCancel: false,
    });
  } catch (error) {
    const message = error instanceof Error ? error.message.toLowerCase() : '';
    if (message.includes('cancel') || message.includes('dismiss')) {
      return;
    }
    throw createError('The report could not be shared.', 'REPORT_SHARE_FAILED');
  }
}

export async function deleteReportFile(localFilePath: string): Promise<void> {
  if (!localFilePath || localFilePath.includes('..')) return;
  try {
    if (await RNFS.exists(localFilePath)) {
      await RNFS.unlink(localFilePath);
    }
  } catch {
    // Ignore delete failures; metadata is cleared by the caller.
  }
}

export async function clearReportDownloadsDirectory(): Promise<void> {
  try {
    const dir = `${RNFS.DocumentDirectoryPath}/${REPORTS_DIR_NAME}`;
    if (await RNFS.exists(dir)) {
      await RNFS.unlink(dir);
    }
  } catch {
    // Best-effort cleanup on sign-out.
  }
}
